/**
 * Create Content Page - Multi-step form
 * Adapted from Next.js: useRouter → useNavigate
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { trpc } from "@/lib/trpc";
import { useCurrentApp } from "@/contexts/app-context";

type Step = 1 | 2 | 3 | 4;

interface FormData {
  title: string;
  body: string;
  platforms: string[];
  scheduleType: "now" | "scheduled";
  scheduledDate?: string;
  scheduledTime?: string;
}

export function ContentNewPage() {
  const navigate = useNavigate();
  const { appId: currentAppId } = useCurrentApp();
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [formData, setFormData] = useState<FormData>({
    title: "",
    body: "",
    platforms: [],
    scheduleType: "now",
  });
  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const createPostMutation = trpc.posts.create.useMutation({
    onSuccess: () => {
      setToast({ type: "success", message: "Post created successfully!" });
      setTimeout(() => {
        navigate("/content");
      }, 1500);
    },
    onError: (error) => {
      setToast({
        type: "error",
        message: error.message || "Failed to create post",
      });
    },
  });

  const handleNext = () => {
    if (currentStep < 4) setCurrentStep((currentStep + 1) as Step);
  };
  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((currentStep - 1) as Step);
  };

  const handleSubmit = async () => {
    if (!currentAppId) {
      setToast({ type: "error", message: "Please select an app first" });
      return;
    }
    let scheduledFor: string | undefined;
    if (formData.scheduleType === "scheduled" && formData.scheduledDate) {
      const time = formData.scheduledTime || "09:00";
      scheduledFor = `${formData.scheduledDate}T${time}:00.000Z`;
    }
    createPostMutation.mutate({
      app_id: currentAppId,
      title: formData.title,
      caption: formData.body,
      platforms: formData.platforms as any,
      scheduled_for: scheduledFor,
    });
  };

  const handleCancel = () => {
    if (confirm("Are you sure you want to discard this draft?"))
      navigate("/content");
  };

  const togglePlatform = (platform: string) => {
    setFormData((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter((p) => p !== platform)
        : [...prev.platforms, platform],
    }));
  };

  const platforms = [
    { id: "twitter", label: "Twitter", icon: "X" },
    { id: "linkedin", label: "LinkedIn", icon: "LI" },
    { id: "instagram", label: "Instagram", icon: "IG" },
    { id: "tiktok", label: "TikTok", icon: "TT" },
    { id: "reddit", label: "Reddit", icon: "R" },
    { id: "email", label: "Email", icon: "EM" },
  ];

  return (
    <div className="min-h-screen bg-bg-primary p-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-primary">
            Create New Post
          </h1>
          <p className="mt-2 text-sm text-text-secondary">
            Follow the steps below to create and schedule your content
          </p>
        </div>

        <div className="mb-8">
          <nav aria-label="Progress">
            <ol className="flex items-center">
              {[
                { step: 1, label: "Content" },
                { step: 2, label: "Platforms" },
                { step: 3, label: "Schedule" },
                { step: 4, label: "Review" },
              ].map((item, index) => (
                <li key={item.step} className="flex-1">
                  <div className="flex items-center">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${currentStep >= item.step ? "border-primary-500 bg-primary-500 text-white" : "border-border-default bg-bg-secondary text-text-tertiary"}`}
                      aria-current={
                        currentStep === item.step ? "step" : undefined
                      }
                    >
                      {item.step}
                    </div>
                    <span
                      className={`ml-2 text-sm font-medium ${currentStep >= item.step ? "text-text-primary" : "text-text-tertiary"}`}
                    >
                      {item.label}
                    </span>
                    {index < 3 && (
                      <div
                        className={`mx-4 h-0.5 flex-1 ${currentStep > item.step ? "bg-primary-500" : "bg-border-default"}`}
                      />
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </nav>
        </div>

        <div className="rounded-lg border border-border-default bg-bg-secondary p-6">
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-text-primary">
                Content Details
              </h2>
              <div>
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-text-secondary mb-2"
                >
                  Title <span className="text-error-500">*</span>
                </label>
                <input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full rounded-md border border-border-default bg-bg-tertiary px-4 py-2 text-text-primary placeholder-text-tertiary focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter post title..."
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="body"
                  className="block text-sm font-medium text-text-secondary mb-2"
                >
                  Content <span className="text-error-500">*</span>
                </label>
                <textarea
                  id="body"
                  value={formData.body}
                  onChange={(e) =>
                    setFormData({ ...formData, body: e.target.value })
                  }
                  rows={8}
                  className="w-full rounded-md border border-border-default bg-bg-tertiary px-4 py-2 text-text-primary placeholder-text-tertiary focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Write your post content..."
                  required
                />
                <p className="mt-2 text-xs text-text-tertiary">
                  {formData.body.length} characters
                </p>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-text-primary">
                Select Platforms
              </h2>
              <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <legend className="sr-only">Select platforms</legend>
                {platforms.map((platform) => (
                  <label
                    key={platform.id}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${formData.platforms.includes(platform.id) ? "border-primary-500 bg-primary-500/10" : "border-border-default bg-bg-tertiary hover:bg-bg-elevated"}`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.platforms.includes(platform.id)}
                      onChange={() => togglePlatform(platform.id)}
                      className="h-5 w-5 rounded border-border-default text-primary-500"
                    />
                    <span className="text-lg font-bold" aria-hidden="true">
                      {platform.icon}
                    </span>
                    <span className="text-sm font-medium text-text-primary">
                      {platform.label}
                    </span>
                  </label>
                ))}
              </fieldset>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-text-primary">
                Schedule Publication
              </h2>
              <div className="space-y-4">
                <label className="flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer border-border-default bg-bg-tertiary">
                  <input
                    type="radio"
                    name="scheduleType"
                    value="now"
                    checked={formData.scheduleType === "now"}
                    onChange={() =>
                      setFormData({ ...formData, scheduleType: "now" })
                    }
                    className="mt-0.5 h-5 w-5 text-primary-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      Post Now
                    </p>
                    <p className="text-sm text-text-secondary">
                      Publish immediately after approval
                    </p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer border-border-default bg-bg-tertiary">
                  <input
                    type="radio"
                    name="scheduleType"
                    value="scheduled"
                    checked={formData.scheduleType === "scheduled"}
                    onChange={() =>
                      setFormData({ ...formData, scheduleType: "scheduled" })
                    }
                    className="mt-0.5 h-5 w-5 text-primary-500"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-text-primary mb-3">
                      Schedule for Later
                    </p>
                    {formData.scheduleType === "scheduled" && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label
                            htmlFor="scheduled-date"
                            className="block text-xs font-medium text-text-secondary mb-1"
                          >
                            Date
                          </label>
                          <input
                            id="scheduled-date"
                            type="date"
                            value={formData.scheduledDate || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                scheduledDate: e.target.value,
                              })
                            }
                            className="w-full rounded-md border border-border-default bg-bg-elevated px-3 py-2 text-sm text-text-primary"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="scheduled-time"
                            className="block text-xs font-medium text-text-secondary mb-1"
                          >
                            Time
                          </label>
                          <input
                            id="scheduled-time"
                            type="time"
                            value={formData.scheduledTime || "09:00"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                scheduledTime: e.target.value,
                              })
                            }
                            className="w-full rounded-md border border-border-default bg-bg-elevated px-3 py-2 text-sm text-text-primary"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-text-primary">
                Review & Submit
              </h2>
              <div className="rounded-lg border border-border-default bg-bg-tertiary p-4 space-y-4">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-text-tertiary mb-1">
                    Title
                  </p>
                  <p className="text-sm text-text-primary">
                    {formData.title || "(No title)"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-text-tertiary mb-1">
                    Content
                  </p>
                  <p className="text-sm text-text-primary whitespace-pre-wrap">
                    {formData.body || "(No content)"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-text-tertiary mb-1">
                    Platforms
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {formData.platforms.length > 0 ? (
                      formData.platforms.map((p) => (
                        <span
                          key={p}
                          className="inline-flex items-center rounded-md bg-bg-elevated px-2.5 py-1 text-xs font-medium text-text-secondary border border-border-subtle capitalize"
                        >
                          {p}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-text-tertiary">
                        (No platforms selected)
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-text-tertiary mb-1">
                    Schedule
                  </p>
                  <p className="text-sm text-text-primary">
                    {formData.scheduleType === "now"
                      ? "Post immediately"
                      : "Scheduled for later"}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between border-t border-border-default pt-6">
            <button
              onClick={handleCancel}
              className="rounded-md border border-border-default bg-bg-tertiary px-4 py-2 text-sm font-medium text-text-primary hover:bg-bg-elevated transition-colors"
              type="button"
            >
              Cancel
            </button>
            <div className="flex gap-3">
              {currentStep > 1 && (
                <button
                  onClick={handleBack}
                  className="rounded-md border border-border-default bg-bg-tertiary px-4 py-2 text-sm font-medium text-text-primary hover:bg-bg-elevated transition-colors"
                  type="button"
                >
                  Back
                </button>
              )}
              {currentStep < 4 ? (
                <button
                  onClick={handleNext}
                  disabled={
                    (currentStep === 1 &&
                      (!formData.title || !formData.body)) ||
                    (currentStep === 2 && formData.platforms.length === 0)
                  }
                  className="rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  type="button"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={createPostMutation.isPending}
                  className="rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  type="button"
                >
                  {createPostMutation.isPending
                    ? "Submitting..."
                    : "Submit for Review"}
                </button>
              )}
            </div>
          </div>
        </div>

        {toast && (
          <div className="fixed bottom-4 right-4 z-50">
            <div
              className={`rounded-lg px-4 py-3 shadow-lg ${toast.type === "success" ? "border border-primary-500/50 bg-primary-950/90 text-primary-400" : "border border-red-500/50 bg-red-950/90 text-red-400"}`}
            >
              <div className="flex items-center gap-3">
                <p className="text-sm font-medium">{toast.message}</p>
                <button
                  onClick={() => setToast(null)}
                  className="ml-2 rounded-md p-1 hover:bg-white/10"
                >
                  X
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
